import {
  ALLOWED_PHOTO_TYPES,
  MAX_PHOTO_BYTES,
  type CreateSubmissionRequest,
  type CreateSubmissionResponse,
  type ListSubmissionsResponse,
  type PublicSubmission,
  type UploadUrlResponse,
} from '../shared/types';

/**
 * Unset in dev, so requests go to `/api` and Vite's proxy forwards them to the
 * local dev server running the same handler. In test/prod, PUBLIC_API_BASE is
 * inlined at build time (see vite.config.ts envPrefix + scripts/deploy.sh).
 */
export const BASE = import.meta.env.PUBLIC_API_BASE ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The one place that talks to the API and parses its error bodies. `headers` is
 * for callers that need to authenticate (the admin routes send `X-Admin-Token`);
 * a second fetch wrapper would mean a second, drifting copy of the error
 * handling below.
 */
export async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown; headers?: Record<string, string> } = {},
): Promise<T> {
  const { method = 'GET', body, headers } = opts;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // A gateway-level failure (429, 502) can return HTML or an empty body, so a
  // parse error must not mask the status the caller needs to see.
  const text = await res.text();
  let payload: { error?: string } = {};
  try {
    payload = text ? (JSON.parse(text) as { error?: string }) : {};
  } catch {
    /* fall through to the status-based message */
  }

  if (!res.ok) {
    throw new ApiError(res.status, payload.error ?? `Request failed (${res.status})`);
  }
  return payload as T;
}

/** Fetch the approved public wall. */
export const listSubmissions = (cursor?: string) =>
  request<ListSubmissionsResponse>(
    `/submissions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
  );

/** How many recent faces the homepage teaser shows. */
export const TEASER_SIZE = 6;

/**
 * The homepage teaser's fetch: reuses the first page from listSubmissions()
 * but only ever needs a handful of recent faces, never a cursor. The public
 * API has no count endpoint, so this never fetches or claims a total -- it
 * just hands back up to `limit` of the most recent approved submissions.
 */
export async function listRecentSubmissions(limit = TEASER_SIZE): Promise<PublicSubmission[]> {
  const { submissions } = await listSubmissions();
  return submissions.slice(0, limit);
}

/** Create a submission. It lands as `pending` and is not publicly visible yet. */
export const createSubmission = (body: CreateSubmissionRequest) =>
  request<CreateSubmissionResponse>('/submissions', { method: 'POST', body });

/**
 * Validate a file against the same limits the server enforces, so an oversized
 * or wrong-typed file fails instantly instead of after a round trip.
 * Returns an error message, or null when the file is acceptable.
 */
export function validatePhoto(file: File): string | null {
  if (!(ALLOWED_PHOTO_TYPES as readonly string[]).includes(file.type)) {
    return 'Photos must be a JPEG, PNG, WebP, or HEIC image.';
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return `That photo is too large. The limit is ${MAX_PHOTO_BYTES / 1024 / 1024}MB.`;
  }
  return null;
}

/**
 * Upload a photo: ask the API for a presigned PUT, then send the bytes straight
 * to S3 rather than through the API. Resolves to the object key to hand to
 * createSubmission.
 *
 * Because the bytes go to the uploads bucket's own S3 origin — not to the API
 * and not through CloudFront — that origin must be listed in the CSP's
 * connect-src (see scripts/setup.sh). Without it the browser refuses the
 * request before sending it and `fetch` rejects with a bare TypeError, which
 * Safari surfaces as "Load failed" and reads like a network fault.
 *
 * Pass the file you actually intend to send. If it was compressed
 * (src/lib/photo.ts), pass the COMPRESSED File: the presigned URL signs
 * `content-length`, so describing one file and PUTting another is a 403
 * SignatureDoesNotMatch.
 */
export async function uploadPhoto(file: File): Promise<string> {
  const invalid = validatePhoto(file);
  if (invalid) throw new ApiError(400, invalid);

  // Read size and type once: these two values are what gets signed, and they
  // must describe the exact object handed to the PUT below.
  const { size: contentLength, type: contentType } = file;

  const { uploadUrl, key } = await request<UploadUrlResponse>('/upload-url', {
    method: 'POST',
    body: { contentType, contentLength },
  });

  // Content-Type must match what was signed or S3 rejects the PUT.
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  });

  if (!put.ok) {
    throw new ApiError(put.status, `Photo upload failed (${put.status})`);
  }

  return key;
}
